import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { formatBRL, cn } from "@/lib/utils";
import type { DayDetail } from "@/data/types";

export function DayDetailModal({ date, onClose }: { date: string | null; onClose: () => void }) {
  const open = !!date;

  const q = useQuery({
    queryKey: ["day-detail", date],
    queryFn: () => client.getDayDetail({ date: date! }),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const detail = q.data;
  const dateLabel = new Date(date!).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div className="relative z-10 max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-xl border border-border-strong bg-bg-card shadow-2xl">
        {/* Header */}
        <header className="flex items-start justify-between border-b border-border-subtle px-7 py-5">
          <div className="space-y-1">
            <div className="text-[11px] font-medium uppercase tracking-[0.15em] text-brand-amber">
              Detalhamento do dia
            </div>
            <h2 className="text-xl font-semibold tracking-tight text-text-primary capitalize">{dateLabel}</h2>
            {detail && (
              <p className="text-[13px] text-text-muted">
                <span className="font-semibold text-text-primary">{detail.totalSales}</span> vendas ·
                <span className="ml-1 font-semibold text-brand-amber">{formatBRL(detail.totalAmount)}</span>
                {" "}em faturamento
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-lg text-text-secondary transition hover:border-border-strong hover:bg-bg-card-hover hover:text-text-primary"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        {/* Content */}
        <div className="max-h-[calc(85vh-88px)] overflow-y-auto px-7 py-6 space-y-7">
          {q.isLoading && (
            <div className="py-10 text-center text-sm text-text-muted">Carregando detalhamento…</div>
          )}

          {detail && detail.products.length === 0 && (
            <div className="rounded-md border border-border-subtle bg-bg-primary py-10 text-center text-sm text-text-muted">
              Nenhuma venda registrada neste dia.
            </div>
          )}

          {detail && detail.products.length > 0 && (
            <>
              <ProductsBlock detail={detail} />
              <SellersBlock detail={detail} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductsBlock({ detail }: { detail: DayDetail }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-tight text-text-primary">Produtos vendidos no dia</h3>
        <span className="text-xs text-text-muted">{detail.products.length} SKU{detail.products.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="overflow-hidden rounded-md border border-border-subtle">
        <table className="w-full">
          <thead className="bg-bg-secondary">
            <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
              <th className="px-5 py-2.5 text-left">Produto</th>
              <th className="px-5 py-2.5 text-left">Categoria</th>
              <th className="px-5 py-2.5 text-right">Qtd.</th>
              <th className="px-5 py-2.5 text-right">Ticket</th>
              <th className="px-5 py-2.5 text-right">Valor vendido</th>
            </tr>
          </thead>
          <tbody>
            {detail.products.map((p, i) => (
              <tr
                key={p.productId}
                className={cn("text-sm", i > 0 && "border-t border-border-subtle")}
              >
                <td className="px-5 py-3 font-medium text-text-primary">{p.productName}</td>
                <td className="px-5 py-3 text-text-secondary">{p.category}</td>
                <td className="px-5 py-3 text-right font-medium text-text-primary">{p.unitsSold}</td>
                <td className="px-5 py-3 text-right text-text-secondary">{formatBRL(p.ticket)}</td>
                <td className="px-5 py-3 text-right font-semibold text-brand-amber">{formatBRL(p.amountSold)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SellersBlock({ detail }: { detail: DayDetail }) {
  const topAmount = Math.max(...detail.sellers.map((s) => s.amountInDay), 1);
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-tight text-text-primary">Vendedores que venderam no dia</h3>
        <span className="text-xs text-text-muted">{detail.sellers.length} closer{detail.sellers.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {detail.sellers.map((s) => (
          <div key={s.userId} className="rounded-md border border-border-subtle bg-bg-primary p-4 space-y-3">
            <div className="flex items-center gap-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
                style={{ backgroundColor: s.avatarColor }}
              >
                {getInitials(s.name)}
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium text-text-primary">{s.name}</div>
                <div className="text-xs text-text-muted">{s.sales} venda{s.sales !== 1 ? "s" : ""} · {formatBRL(s.amountInDay)} no dia</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-text-muted">Acumulado YTD</span>
                <span className="font-semibold text-text-primary">{formatBRL(s.amountAccumulated)}</span>
              </div>
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-bg-card">
                <div
                  className="h-full rounded-full bg-brand-amber"
                  style={{ width: `${(s.amountInDay / topAmount) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
